import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon, Button, Badge } from '@/components/atoms';
import { Input } from '@kisanlink/ui-commons';
import { usePurchaseOrdersList } from '@/hooks/useErpApi';
import type { PurchaseOrder } from '@/schemas/purchaseOrders';

interface PurchaseOrderListProps {
  onCreateClick: () => void;
}

export const PurchaseOrderList: React.FC<PurchaseOrderListProps> = ({ onCreateClick }) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(25);

  // Calculate offset for API call
  const offset = (currentPage - 1) * itemsPerPage;
  const { data: purchaseOrders, isLoading, error, refetch } = usePurchaseOrdersList({
    limit: itemsPerPage,
    offset,
    ...(selectedStatus !== 'all' && { status: selectedStatus }),
  });

  const statuses = ['all', 'placed', 'confirmed', 'out_for_delivery', 'delivered', 'verified', 'paid'];

  // Client-side filtering for search
  const filteredPOs = useMemo(() => {
    return (purchaseOrders || []).filter((po) => {
      const matchesSearch =
        po.po_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
        po.collaborator_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        po.warehouse_name.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesSearch;
    });
  }, [purchaseOrders, searchQuery]);

  // Reset to page 1 when search or status changes
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };
  
  const handleStatusChange = (status: string) => {
    setSelectedStatus(status);
    setCurrentPage(1);
  };

  const getStatusBadge = (status: PurchaseOrder['status']) => {
    switch (status) {
      case 'placed':
        return <Badge variant="info">Placed</Badge>;
      case 'confirmed':
        return <Badge variant="warning">Confirmed</Badge>;
      case 'out_for_delivery':
        return <Badge variant="secondary">Out for Delivery</Badge>;
      case 'delivered':
        return <Badge variant="success">Delivered</Badge>;
      case 'verified':
        return <Badge variant="success">Verified</Badge>;
      case 'paid':
        return <Badge variant="secondary">Paid</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getStatusColor = (status: PurchaseOrder['status']) => {
    switch (status) {
      case 'placed':
        return 'bg-info-600';
      case 'confirmed':
        return 'bg-warning-600';
      case 'out_for_delivery':
        return 'bg-purple-600';
      case 'delivered':
        return 'bg-success-600';
      case 'verified':
        return 'bg-success-700';
      case 'paid':
        return 'bg-neutral-600';
      default:
        return 'bg-neutral-400';
    }
  };

  const handleRowClick = (id: string) => {
    navigate(`/purchase-orders/${id}`);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <Icon name="Package" size={48} className="text-neutral-400 mx-auto mb-4" />
          <p className="text-lg font-medium text-neutral-600">Loading purchase orders...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-error-50 border border-error-200 rounded-lg p-6">
          <div className="flex items-start gap-4">
            <div className="bg-error-100 p-2 rounded-lg">
              <Icon name="CircleAlert" size={24} className="text-error-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-error-900 mb-1">
                Failed to Load Purchase Orders
              </h3>
              <p className="text-error-700 mb-3">
                Unable to fetch purchase orders data. Please try again.
              </p>
              <Button variant="primary" size="sm" onClick={() => refetch()}>
                <Icon name="RefreshCw" size={16} className="mr-2" />
                Retry
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-neutral-900">Purchase Orders</h1>
          <p className="text-neutral-600 mt-2">Manage supplier purchase orders</p>
        </div>
        <Button variant="primary" className="sm:w-auto" onClick={onCreateClick}>
          <Icon name="Plus" size={20} className="mr-2" />
          Create Purchase Order
        </Button>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Icon
                name="Search"
                size={20}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400"
              />
              <Input
                type="text"
                placeholder="Search by PO number, supplier, or warehouse..."
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {statuses.map((status) => (
              <button
                key={status}
                onClick={() => handleStatusChange(status)}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                  selectedStatus === status
                    ? 'bg-primary-600 text-white shadow-sm'
                    : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                }`}
              >
                {status === 'all'
                  ? 'All'
                  : status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
              </button>
            ))}
          </div>
        </div>
      </div>

      {filteredPOs.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-12 text-center">
          <Icon name="Package" size={64} className="text-neutral-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-neutral-900 mb-2">No Purchase Orders Found</h3>
          <p className="text-neutral-600 mb-4">
            {searchQuery || selectedStatus !== 'all'
              ? 'Try adjusting your filters'
              : 'Create your first purchase order to get started'}
          </p>
          {!searchQuery && selectedStatus === 'all' && (
            <Button variant="primary" onClick={onCreateClick}>
              <Icon name="Plus" size={20} className="mr-2" />
              Create Purchase Order
            </Button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-neutral-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-neutral-50 border-b border-neutral-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    PO Number
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Supplier
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Warehouse
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Order Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Expected Delivery
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Total Amount
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-neutral-200">
                {filteredPOs.map((po) => (
                  <tr
                    key={po.id}
                    onClick={() => handleRowClick(po.id)}
                    className="hover:bg-neutral-50 cursor-pointer transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div
                          className={`w-2 h-2 rounded-full ${getStatusColor(po.status as PurchaseOrder['status'])} mr-3`}
                        ></div>
                        <span className="text-sm font-medium text-neutral-900">{po.po_number}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-neutral-900">{po.collaborator_name}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-neutral-900">{po.warehouse_name}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(po.status as PurchaseOrder['status'])}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-neutral-600">
                        {new Date(po.order_date).toLocaleDateString()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-neutral-600">
                        {new Date(po.expected_delivery_date).toLocaleDateString()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <span className="text-sm font-medium text-neutral-900">
                        ₹{po.total_amount.toFixed(2)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
